/**
 * Wysyła zdarzenia analityczne: Google Analytics + własne liczniki na serwerze.
 * Ciasteczko identyfikacyjne 12go_vid ustawia endpoint /api/stats/event.
 */

import { LOCALES } from "@/lib/localeCatalog";

export type AffiliateClickParams = {
  partnerId: string;
  partnerLabel: string;
  destinationUrl: string;
  /** Opcjonalnie: kontekst, np. "homepage" | "where_to_buy" | "product_detail" */
  context?: string;
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function isAdminAnalyticsSkip(): boolean {
  if (typeof document === "undefined") return false;
  return /(?:^|;\s*)12go_admin_skip=1(?:;|$)/.test(document.cookie);
}

function postStatsEvent(payload: Record<string, string>): void {
  if (typeof window === "undefined") return;
  if (isAdminAnalyticsSkip()) return;
  void fetch("/api/stats/event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => undefined);
}

export function trackAffiliateClick({
  partnerId,
  partnerLabel,
  destinationUrl,
  context,
}: AffiliateClickParams): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "affiliate_click", {
      partner_id: partnerId,
      link_label: partnerLabel,
      link_url: destinationUrl,
      ...(context && { link_context: context }),
    });
  }
  postStatsEvent({
    type: "affiliate_click",
    partnerId,
    ...(context ? { context } : {}),
  });
}

export function trackProductView(productId: string): void {
  postStatsEvent({ type: "product_view", productId });
}

export function trackPromoImpression(promoId: string): void {
  if (!promoId) return;
  postStatsEvent({ type: "promo_impression", promoId });
}

export function trackPromoClick(promoId: string): void {
  if (!promoId) return;
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "promo_click", { promo_id: promoId });
  }
  postStatsEvent({ type: "promo_click", promoId });
}

export function trackPromoDismiss(promoId: string): void {
  if (!promoId) return;
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "promo_dismiss", { promo_id: promoId });
  }
  postStatsEvent({ type: "promo_dismiss", promoId });
}

export function trackBuyCta(productId: string): void {
  postStatsEvent({ type: "buy_cta", productId });
}

export function trackProductOfferClick({
  productId,
  partnerId,
  partnerLabel,
  destinationUrl,
}: {
  productId: string;
  partnerId: string;
  partnerLabel: string;
  destinationUrl: string;
}): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "product_offer_click", {
      product_id: productId,
      partner_id: partnerId,
      link_label: partnerLabel,
      link_url: destinationUrl,
      link_context: "product_offer",
    });
  }
  postStatsEvent({
    type: "product_offer_click",
    productId,
    partnerId,
  });
}

const LANDING_FLAG = "12go_src";
let landingSent = false;

function detectDevice(): "mobile" | "tablet" | "desktop" {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet|PlayBook/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) {
    return "tablet";
  }
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
}

function classifyHost(host: string): string {
  const h = host.replace(/^www\./i, "").toLowerCase();
  if (
    h === "google.com" ||
    h.endsWith(".google.com") ||
    /^google\.[a-z.]+$/.test(h) ||
    h.endsWith(".googleusercontent.com")
  ) {
    return "google";
  }
  if (h === "bing.com" || h.endsWith(".bing.com") || /^bing\.[a-z.]+$/.test(h)) return "bing";
  if (
    /(?:^|\.)(duckduckgo\.com|yahoo\.com|baidu\.com|yandex\.(?:com|ru)|ecosia\.org|brave\.com|startpage\.com)$/.test(
      h,
    )
  ) {
    return "search";
  }
  if (
    /(?:^|\.)(facebook\.com|fb\.com|instagram\.com|twitter\.com|x\.com|t\.co|tiktok\.com|reddit\.com|pinterest\.com|youtube\.com|youtu\.be|linkedin\.com|whatsapp\.com)$/.test(
      h,
    )
  ) {
    return "social";
  }
  return h.replace(/[^a-z0-9._-]/g, "").slice(0, 40) || "other";
}

function takeLandingSource(): string | undefined {
  if (landingSent) return undefined;
  landingSent = true;
  try {
    if (sessionStorage.getItem(LANDING_FLAG)) return undefined;
    sessionStorage.setItem(LANDING_FLAG, "1");
  } catch {
    /* private mode — landingSent already blocks the rest of this tab */
  }

  const utm = new URLSearchParams(window.location.search).get("utm_source");
  if (utm) {
    const cleaned = utm.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "").slice(0, 40);
    if (cleaned) return cleaned;
  }

  const referrer = document.referrer.trim();
  if (!referrer) return "direct";
  try {
    const url = new URL(referrer);
    if (url.hostname.replace(/^www\./i, "").toLowerCase() === window.location.hostname.replace(/^www\./i, "").toLowerCase()) {
      return "internal";
    }
    return classifyHost(url.hostname);
  } catch {
    return "direct";
  }
}

function queryFromAsPath(asPath: string, name: string): string {
  const qIndex = asPath.indexOf("?");
  if (qIndex === -1) return "";
  return new URLSearchParams(asPath.slice(qIndex + 1)).get(name) ?? "";
}

export function trackPageView(asPath: string): void {
  const pathname = asPath.split("?")[0] || "/";
  const segments = pathname.replace(/^\/+/, "").split("/").filter(Boolean);
  const first = segments[0];
  const locale = first && (LOCALES as readonly string[]).includes(first) ? first : "en";
  const device = detectDevice();
  const source = takeLandingSource();
  postStatsEvent({
    type: "page_view",
    locale,
    device,
    ...(source ? { source } : {}),
  });
}

export function trackSearch(rawQuery: string): void {
  const query = rawQuery.replace(/\s+/g, " ").trim().toLowerCase().slice(0, 80);
  if (query.length < 2) return;
  postStatsEvent({ type: "search", query });
}

export function trackCatalogSearchFromPath(asPath: string): void {
  trackSearch(queryFromAsPath(asPath, "q"));
}
